import { Box } from '@mantine/core';
import { memo, useState } from 'react';
import { useAuthUser, useWorkspaceList } from '../../api';
import { isSecodaCoEmail, isSecodaComEmail } from '../../utils/utils';
import { AuthStepWorkspaceCreate } from './AuthStepWorkspaceCreate';
import { AuthStepWorkspaceJoin } from './AuthStepWorkspaceJoin';
import { AuthStepWorkspacePending } from './AuthStepWorkspacePending';
import type { WorkspaceTab } from './consts';
import { WORKSPACE_TAB } from './consts';

function AuthStepWorkspace() {
	const { user } = useAuthUser();
	const { isLoading, data: workspaces } = useWorkspaceList({
		page: 1,
		filters: {
			disabled: false,
			scheduled_for_deletion: false,
			enforce_scim_only_provisioning: false,
		},
		options: {
			select: (data) => data.results,
			refetchOnMount: true,
			refetchOnWindowFocus: true,
			refetchOnReconnect: true,
		},
	});
	const [activeWorkspaceTab, setActiveWorkspaceTab] = useState<WorkspaceTab>(
		WORKSPACE_TAB.JOIN
	);

	// Determine tab based on workspace data
	const hasWorkspaces = (workspaces?.length ?? 0) > 0;

	const shouldShowJoin = !isLoading && hasWorkspaces;

	const shouldShowCreate =
		!isLoading &&
		(isSecodaCoEmail(user?.email) || isSecodaComEmail(user?.email));

	const shouldShowWorkspacePending =
		!isLoading && !shouldShowCreate && !shouldShowJoin;

	const shouldShowToggle = !isLoading && shouldShowJoin && shouldShowCreate;

	return (
		<Box>
			{shouldShowToggle ? (
				<>
					{activeWorkspaceTab === WORKSPACE_TAB.JOIN && (
						<AuthStepWorkspaceJoin
							workspaces={workspaces ?? []}
							setActiveWorkspaceTab={setActiveWorkspaceTab}
						/>
					)}
					{activeWorkspaceTab === WORKSPACE_TAB.CREATE && (
						<AuthStepWorkspaceCreate
							setActiveWorkspaceTab={setActiveWorkspaceTab}
						/>
					)}
				</>
			) : (
				<>
					{shouldShowJoin && (
						<AuthStepWorkspaceJoin workspaces={workspaces ?? []} />
					)}
					{/* Note: Secoda domain users would normally always see existing workspaces to join. This is for dev purposes only. */}
					{shouldShowCreate && <AuthStepWorkspaceCreate />}

					{shouldShowWorkspacePending && <AuthStepWorkspacePending />}
				</>
			)}
		</Box>
	);
}

export default memo(AuthStepWorkspace);
